import CartItem from 'models/CartItem';
import SharedCartItem from 'models/SharedCartItem';
import { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useEditor from './use-editor';

interface GetLinkToItemProps {
  itemId: string;
  menuId: string;
  addEdit?: 'add' | 'edit';
}

export const useOpenItemSheet = () => {
  const { setCartItem } = useEditor();
  const { locationCode } = useParams<{ locationCode: string }>();
  const history = useHistory();

  const getLinkToItem = useCallback(
    ({ itemId, menuId, addEdit = 'add' }: GetLinkToItemProps) =>
      `/${locationCode}/${addEdit}?menu=${menuId}&item=${itemId}`,
    [locationCode]
  );

  const getLinkToAddItem = useCallback(
    (params: GetLinkToItemProps) => getLinkToItem({ ...params, addEdit: 'add' }),
    [getLinkToItem]
  );

  const getLinkToEditItem = useCallback(
    (params: GetLinkToItemProps) => getLinkToItem({ ...params, addEdit: 'edit' }),
    [getLinkToItem]
  );

  const openItemSheet = useCallback(
    (params: GetLinkToItemProps) => history.push(getLinkToItem(params)),
    [history, getLinkToItem]
  );

  const openAddItemSheet = useCallback(
    (params: GetLinkToItemProps) => openItemSheet({ ...params, addEdit: 'add' }),
    [openItemSheet]
  );

  const openAddItemWithDefaults = useCallback(
    (cartItem: CartItem | SharedCartItem) => {
      const { menu_item_id: itemId, menu_id: menuId } = cartItem;
      setCartItem(cartItem);
      setTimeout(() => openAddItemSheet({ itemId, menuId }));
    },
    [setCartItem, openAddItemSheet]
  );

  const openEditItemSheet = useCallback(
    (params: GetLinkToItemProps) => openItemSheet({ ...params, addEdit: 'edit' }),
    [openItemSheet]
  );

  const openEditItemWithDefaults = useCallback(
    (cartItem: CartItem | SharedCartItem) => {
      const { menu_item_id: itemId, menu_id: menuId } = cartItem;
      setCartItem(cartItem);
      setTimeout(() => openEditItemSheet({ itemId, menuId }));
    },
    [setCartItem, openEditItemSheet]
  );

  return {
    getLinkToAddItem,
    getLinkToEditItem,
    openAddItemSheet,
    openAddItemWithDefaults,
    openEditItemSheet,
    openEditItemWithDefaults,
  };
};
